import { RefObject, useCallback, useEffect, useState } from "react";

interface IScrollState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

const SCROLL_STEP_PX = 120;

export const useToolbarScroll = (scrollRef: RefObject<HTMLDivElement | null>, dependencyKey: string) => {
  const [scrollState, setScrollState] = useState<IScrollState>({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      setScrollState({ canScrollLeft: false, canScrollRight: false });
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = element;
    setScrollState({
      canScrollLeft: scrollLeft > 0,
      canScrollRight: scrollLeft + clientWidth < scrollWidth - 1,
    });
  }, [scrollRef]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return undefined;
    }

    updateScrollState();

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(element);
    element.addEventListener("scroll", updateScrollState, { passive: true });

    return () => {
      resizeObserver.disconnect();
      element.removeEventListener("scroll", updateScrollState);
    };
  }, [dependencyKey, scrollRef, updateScrollState]);

  const scrollLeft = useCallback(() => {
    scrollRef.current?.scrollBy({ left: -SCROLL_STEP_PX, behavior: "smooth" });
  }, [scrollRef]);

  const scrollRight = useCallback(() => {
    scrollRef.current?.scrollBy({ left: SCROLL_STEP_PX, behavior: "smooth" });
  }, [scrollRef]);

  return { ...scrollState, scrollLeft, scrollRight };
};
